import React from 'react';
import {Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow} from '@mui/material';
import { RISK_BAND_COLORS } from '../constants';

const NAVY = '#052F44';
const TEAL = '#1B6B55';

/* ── Confidence chip — solid shade, not a pastel tint. Watch matches
   RISK_BAND_COLORS exactly; Low is this tab's own vocabulary. ──────── */
const CONFIDENCE = {
    Low:   '#B91C1C',
    Watch: RISK_BAND_COLORS.Watch,
};

const ConfidenceChip = ({label}) => (
    <Box sx={{
        display: 'inline-block',
        backgroundColor: CONFIDENCE[label] || '#6B7280',
        color: '#ffffff',
        fontSize: '13px',
        fontWeight: 600,
        px: 2.5,
        py: 0.6,
        borderRadius: '20px',
        minWidth: '80px',
        textAlign: 'center',
        whiteSpace: 'nowrap',
    }}>
        {label}
    </Box>
);

/* ── Shared table cell styles ───────────────────────────────────── */
const headCell = {
    backgroundColor: NAVY,
    color: '#ffffff',
    fontSize: '13px',
    fontWeight: 700,
    borderBottom: 'none',
    py: 2,
    px: 2.5,
};

const bodyCell = {
    fontSize: '13px',
    color: '#374151',
    py: 2,
    px: 2.5,
    borderBottom: '1px solid #F3F4F6',
    lineHeight: 1.5,
};

/* ── Table data ─────────────────────────────────────────────────── */
const DQ_ROWS = [
    {
        deployment: 'Manila Pilot Group (PH)',
        issue:      'Low participation (58%) + small sample',
        meaning:    'Below reporting threshold',
        action:     'Re-assess before reporting any score',
        confidence: 'Low',
    },
    {
        deployment: 'West Midlands Pilot (UK)',
        issue:      'Stale data — last activity 5 months',
        meaning:    'Dormant deployment',
        action:     'Reactivate or archive the deployment',
        confidence: 'Low',
    },
    {
        deployment: 'Philippines (country)',
        issue:      'Coverage 61%, uneven across clients',
        meaning:    'Country aggregate is uncertain',
        action:     'Lift coverage before any national claim',
        confidence: 'Watch',
    },
    {
        deployment: 'New Zealand (2 clients)',
        issue:      'No prior-cycle baseline',
        meaning:    'Growth cannot be computed',
        action:     'Collect a baseline next cycle',
        confidence: 'Watch',
    },
    {
        deployment: 'Malaysia',
        issue:      'No refresh in 27 days',
        meaning:    'Freshness flag',
        action:     'Request a data update from the client',
        confidence: 'Watch',
    },
];

const DataQualityTab = () => (
    <Box>
        {/* ── Table ── */}
        <TableContainer sx={{
            border: '1px solid var(--brand-border, #E5E7EB)',
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        }}>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{...headCell, width: '20%'}}>Deployment</TableCell>
                        <TableCell sx={{...headCell, width: '22%'}}>Issue</TableCell>
                        <TableCell sx={{...headCell, width: '20%'}}>What it means</TableCell>
                        <TableCell sx={{...headCell, width: '26%'}}>Action</TableCell>
                        <TableCell sx={{...headCell, width: '12%', textAlign: 'center'}}>Confidence</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {DQ_ROWS.map((row, i) => (
                        <TableRow key={row.deployment} sx={{backgroundColor: i % 2 === 0 ? 'var(--brand-surface, #ffffff)' : 'var(--brand-surface-subtle, #F9FAFB)'}}>
                            <TableCell sx={{...bodyCell, fontWeight: 700, color: NAVY}}>{row.deployment}</TableCell>
                            <TableCell sx={bodyCell}>{row.issue}</TableCell>
                            <TableCell sx={bodyCell}>{row.meaning}</TableCell>
                            <TableCell sx={bodyCell}>{row.action}</TableCell>
                            <TableCell sx={{...bodyCell, textAlign: 'center'}}>
                                <ConfidenceChip label={row.confidence}/>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>

        {/* ── Recommended action footer ── */}
        <Box sx={{
            mt: 3,
            p: {xs: 2, md: 2.5},
            backgroundColor: 'var(--brand-surface-subtle, #F0F7F4)',
            border: '1px solid #C6E0D8',
            borderLeft: `4px solid ${TEAL}`,
            borderRadius: '0 8px 8px 0',
        }}>
            <Typography sx={{fontSize: '13px', color: '#374151', lineHeight: 1.7}}>
                <strong style={{color: TEAL}}>Recommended action:&nbsp;</strong>
                Use this page before any partner reporting or renewal conversation. Performance and impact claims
                should be made only where data confidence is High or Adequate.
            </Typography>
        </Box>
    </Box>
);

export default DataQualityTab;
